import PhotoIcon from 'ui/elements/icons/ImageIcon';
import React from 'react';

import Button from 'ui/elements/buttons/Button';
import ImageUpload, { ImageEditorState, ImageUploadShape } from 'ui/modules/ImageUpload';
import Placeholder from 'ui/modules/ImageUpload/Placeholder';

import { resize } from 'util/cloudinary';

import styles from './styles.scss';
import { CloudinaryUploadResponse, CloudinaryUploadSettings } from 'types';
import useNotify from 'hooks/useNotify';
import cx from 'classnames';
import AvatarEditor from 'react-avatar-editor';

interface Props {
  aspectRatio: number;
  currentImageUrl?: string;
  getUploadUrl?: (filename: string) => Promise<CloudinaryUploadSettings>;
  onRemoveImage: () => void;
  onUploadSuccess?: (image: CloudinaryUploadResponse) => void;
  onConfirmImage?: () => void;
  onChange?: (avatarEditor: AvatarEditor, image?: File) => void;
  width?: number;
  aspectRatioHint?: string;
  resizeToWidth?: number;
  resizeToHeight?: number;
  headerText?: string;
  shape?: ImageUploadShape;
  editorState?: ImageEditorState;
  allowZoomOut?: boolean;
  renderUploadButton?: (openDropzone: () => void) => React.ReactNode;
}

export default function ImageUploader(props: Props) {
  const notify = useNotify();

  return (
    <section>
      {props.headerText && <h3 className="text-subheading-2 u-content-spacing-bottom">{props.headerText}</h3>}
      <ImageUpload
        width={props.width}
        aspectRatio={props.aspectRatio}
        onUploadSuccess={image => (props.onUploadSuccess ? props.onUploadSuccess(image) : null)}
        onUploadFailure={e => {
          notify('error', 'Upload failed. Only jpeg & png images are allowed.');
        }}
        onRemoveImage={props.onRemoveImage}
        onConfirmImage={props.onConfirmImage}
        onChange={props.onChange}
        shape={props.shape}
        getUploadUrl={props.getUploadUrl ? (filename: string) => props.getUploadUrl!(filename) : undefined}
        imageUrl={resize(props.currentImageUrl, { width: props.resizeToWidth, height: props.resizeToHeight })}
        allowZoomOut={props.allowZoomOut}
        placeholder={
          props.shape === 'round' ? (
            <Placeholder shape="round">
              <div className={styles.roundPlaceholder}>
                <PhotoIcon fontSize="large" />
                Drop .png or .jpeg logo here
              </div>
            </Placeholder>
          ) : (
            <Placeholder>Drop your image here or click to select one</Placeholder>
          )
        }
        editorState={props.editorState}
      >
        {openDropzone => (
          <div className={cx(styles.dropzone, styles.horizontalLayout)}>
            {props.renderUploadButton ? (
              props.renderUploadButton(openDropzone)
            ) : (
              <>
                <Button kind="primary" onClick={openDropzone} className="u-content-spacing-right">
                  <span>Upload image</span>
                </Button>
                <p className="text-metadata-regular">
                  {props.aspectRatioHint ||
                    'To upload an image, click to select one or drag your .png or .jpeg file onto the circle.'}
                </p>
              </>
            )}
          </div>
        )}
      </ImageUpload>
    </section>
  );
}
